import { z } from "zod";

import type { components } from "@/api/schema";
import { moneyStringSchema, parseMoneyAmount } from "@/shared/lib/money";

const MARKETPLACE = ["shopee", "lazada", "tiktok"] as const;
// min_spend_value is never negative (unlike settlement `fees`) — see the sibling
// simulate-schema.ts for the same field-level pattern. Field-level (not a
// superRefine on ruleSchema) so it applies to both ruleSchema and draftRuleSchema.
const money = moneyStringSchema("promotion:builder.err_money_format").refine(
  (v) => parseMoneyAmount(v) >= 0,
  "promotion:builder.err_money_negative",
);

/** RuleProduct — mirrors components["schemas"]["RuleProduct"]. */
export const ruleProductSchema = z.object({
  sku: z.string().trim().min(1, "promotion:builder.err_sku_required"),
  kind: z.enum(["trigger", "add_on", "constraint", "exclude_min_spend"]),
  quantity: z.number().int().min(1).nullable().optional(),
  is_freebie: z.boolean().default(false),
});

export type RuleProductValues = z.infer<typeof ruleProductSchema>;
export type RuleProductInput = z.input<typeof ruleProductSchema>;

/**
 * RuleUpsert — one builder row. Cross-field rules:
 * - is_min_spend gate: when true, min_spend_value must be > 0.
 * - must have ≥1 trigger product and ≥1 add_on (gift) product.
 */
/**
 * Bare RuleUpsert shape (no cross-field requirements). Both `ruleSchema` (strict,
 * "ready to activate") and `draftRuleSchema` (lenient, save-in-progress) build on
 * this so the two never drift on field-level types.
 */
const ruleObjectSchema = z.object({
  // BE issues the id (any UUID variant); the builder echoes it back unchanged
  // on update. Treat it as an opaque non-empty string rather than validating a
  // specific UUID version so hydrated server rows always round-trip.
  id: z.string().min(1).nullable().optional(),
  sequence: z.number().int().min(0),
  rule_type: z.enum(["all", "break"]),
  payment_method: z.enum(["ALL", "COD", "NON_COD"]),
  min_spend_value: money,
  is_min_spend: z.boolean(),
  discount: z.enum(["include", "exclude"]),
  shipping_fee: z.enum(["include", "exclude"]),
  gift_limit_quantity: z.number().int().min(1, "precheck.reason.quota_zero"),
  gift_limit_type: z.enum(["PER_ORDER", "PER_ITEM"]),
  // Gift units already consumed on the legacy system (SynDi) before this rule
  // was recreated in Atlas. Counted toward gift_used at cutover.
  gift_used_offset: z
    .number()
    .int()
    .min(0, "promotion:builder.err_gift_used_offset_negative")
    .default(0),
  // Read-only per-rule usage hydrated from the detail payload. Not part of the
  // upsert body (omitted in PromotionBuilder.onSubmit); kept on the form so the
  // RuleCard can render the quota bar. `gift_remaining` is undefined when the
  // rule is uncapped (gift_limit_quantity = 0 on the wire).
  gift_used: z.number().int().min(0).optional(),
  gift_remaining: z.number().int().min(0).nullable().optional(),
  products: z.array(ruleProductSchema),
});

/**
 * RuleUpsert — one builder row. Cross-field rules:
 * - is_min_spend gate: when true, min_spend_value must be > 0.
 * - must have ≥1 trigger product and ≥1 add_on (gift) product.
 *
 * These are the **activation** requirements. The builder's Save/Save-draft uses
 * the lenient `draftRuleSchema` instead so work-in-progress persists.
 */
export const ruleSchema = ruleObjectSchema.superRefine((rule, ctx) => {
  if (rule.is_min_spend && parseMoneyAmount(rule.min_spend_value) <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["min_spend_value"],
      message: "promotion:builder.err_min_spend_required",
    });
  }
  // A rule needs a gate: either specific trigger SKU(s), or — for a storewide
  // ("All products") rule — a min-spend threshold. Empty triggers alone is valid
  // only when min-spend is set (sino: storewide promos rely on min-spend).
  const hasTrigger = rule.products.some((p) => p.kind === "trigger");
  if (!hasTrigger && !(rule.is_min_spend && parseMoneyAmount(rule.min_spend_value) > 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["products"],
      message: "promotion:builder.err_no_trigger_or_min_spend",
    });
  }
  if (!rule.products.some((p) => p.kind === "add_on")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["products"],
      message: "promotion:builder.err_no_gift",
    });
  }
});

export type RuleValues = z.infer<typeof ruleSchema>;
export type RuleInput = z.input<typeof ruleSchema>;

/**
 * Lenient rule for Save / Save-draft: field-level types still apply (so the upsert
 * body stays well-typed), but the trigger/gift/min-spend completeness checks do NOT
 * run. A half-built rule must persist; completeness is enforced only at Activate
 * (BE precheck → `no_rules`).
 */
export const draftRuleSchema = ruleObjectSchema;

/**
 * The end-after-start date check applies to both Save and Activate, so it lives in
 * a shared refinement helper. Returns a date issue when end ≤ start.
 */
function refineDates(promo: { start_at: string; end_at: string }, ctx: z.RefinementCtx): void {
  if (Date.parse(promo.end_at) <= Date.parse(promo.start_at)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["end_at"],
      message: "promotion:builder.err_dates",
    });
  }
}

/** Shared object shape for the upsert body (basics + targeting + rule_mode). */
const promotionBaseShape = {
  name: z.string().trim().min(1, "promotion:builder.err_name_required"),
  note: z.string().optional(),
  // Bilingual notes (mockup Basics: Description EN / ไทย). Both optional and
  // accepted by the upsert request (note_en / note_th); the legacy single
  // `note` is retained alongside per the contract.
  note_en: z.string().optional(),
  note_th: z.string().optional(),
  marketplace: z.enum(MARKETPLACE),
  seller_id: z.string().trim().min(1, "promotion:builder.err_seller_required"),
  start_at: z.string().min(1, "promotion:builder.err_dates"),
  end_at: z.string().min(1, "promotion:builder.err_dates"),
  rule_mode: z.enum(["all", "break"]),
} as const;

/**
 * STRICT — "ready to activate". Requires valid basics + dates, ≥1 rule, and each
 * rule complete (trigger + gift). Used by activation checks and the schema tests;
 * NOT by the builder's Save (which must persist work-in-progress).
 */
export const promotionSchema = z
  .object({ ...promotionBaseShape, rules: z.array(ruleSchema) })
  .superRefine((promo, ctx) => {
    if (promo.rules.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rules"],
        message: "promotion:builder.err_no_rules",
      });
    }
    refineDates(promo, ctx);
  });

/**
 * LENIENT — the builder's Save / Save-draft schema. Requires only the BASICS to be
 * valid (name, marketplace, seller_id, end-after-start). Rules are optional and, if
 * present, are NOT required to carry a trigger/gift (`draftRuleSchema`). This mirrors
 * the BE upsert (PUT /v1/promotions/{id}), which accepts `rules: []` and incomplete
 * rules — rule completeness is enforced only at Activate.
 */
export const promotionDraftSchema = z
  .object({ ...promotionBaseShape, rules: z.array(draftRuleSchema) })
  .superRefine((promo, ctx) => {
    refineDates(promo, ctx);
  });

export type PromotionFormValues = z.infer<typeof promotionSchema>;
/**
 * Form *input* type (what `useForm` defaultValues / register feed in). Differs
 * from `PromotionFormValues` (the parsed output) wherever the schema applies a
 * `.default()` (e.g. `is_freebie`) or an `.optional()`, so RHF's resolver
 * generic must be `<Input, _, Output>` — mirrors inventory's
 * `create-sku-bundle-schema` (CreateSkuBundleFormInput / FormValues).
 */
export type PromotionFormInput = z.input<typeof promotionSchema>;

/**
 * The builder binds to the lenient draft schema, so its form input/output types come
 * from it. The two schemas share `promotionBaseShape` and the same rule field types,
 * so a value valid against the strict schema is assignable to the draft one.
 */
export type PromotionDraftFormValues = z.infer<typeof promotionDraftSchema>;
export type PromotionDraftFormInput = z.input<typeof promotionDraftSchema>;

/**
 * Runtime/compile bridge: the form value maps to the BE upsert body. We model the
 * required keys explicitly rather than `extends` because `exactOptionalPropertyTypes`
 * makes a structural `extends` over optional fields (note/quantity) brittle.
 */
type UpsertBody = components["schemas"]["PromotionUpsertRequest"];
type _AssertKeys = keyof PromotionFormValues extends keyof UpsertBody ? true : never;
const _keys: _AssertKeys = true;
void _keys;
type _AssertDraftKeys = keyof PromotionDraftFormValues extends keyof UpsertBody ? true : never;
const _draftKeys: _AssertDraftKeys = true;
void _draftKeys;
// Value-level guard: a rule's min_spend_value must stay assignable to the BE's
// RuleUpsert.min_spend_value (string) — catches a future backend type drift
// at compile time instead of silently passing keyof-only checks.
type _AssertMinSpend =
  RuleValues["min_spend_value"] extends components["schemas"]["RuleUpsert"]["min_spend_value"]
    ? true
    : never;
const _minSpend: _AssertMinSpend = true;
void _minSpend;
